import React, { useEffect, useRef, useState } from "react";

function useInView() {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && (setInView(true), io.disconnect())),
      { threshold: 0.1 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return [ref, inView];
}

const reveal = (inView, delay = 0) => ({
  opacity: inView ? 1 : 0,
  transform: inView ? "translateY(0)" : "translateY(12px)",
  transition: `opacity 480ms ease ${delay}ms, transform 480ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
});

const muted = "rgba(26,26,25,0.55)";
const body = "rgba(26,26,25,0.78)";

function Section({ title, children, testid }) {
  const [ref, inView] = useInView();
  return (
    <section
      ref={ref}
      className="border-t pt-12 sm:pt-14"
      style={{ borderColor: "rgba(26,26,25,0.10)" }}
      data-testid={testid}
    >
      <h2
        className="font-display tracking-[0.22em] uppercase"
        style={{
          fontSize: "clamp(0.95rem, 1.1vw, 1.05rem)",
          fontWeight: 500,
          color: "#0E0E0D",
          ...reveal(inView, 0),
        }}
      >
        {title}
      </h2>
      <div
        className="mt-6 text-base sm:text-[16.5px] leading-[1.95] max-w-[640px] space-y-4"
        style={{ color: body, ...reveal(inView, 120) }}
      >
        {children}
      </div>
    </section>
  );
}

export default function ShippingDelivery() {
  const [topRef, topInView] = useInView();

  return (
    <main
      ref={topRef}
      className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-14 pt-20 sm:pt-28 pb-24 sm:pb-32"
      data-testid="shipping-delivery-page"
    >
      {/* Header */}
      <div className="max-w-[820px]">
        <h1
          className="font-display leading-[1.05]"
          style={{
            fontSize: "clamp(2rem, 4vw, 3.25rem)",
            letterSpacing: "-0.005em",
            fontWeight: 400,
            ...reveal(topInView, 0),
          }}
          data-testid="sd-heading"
        >
          Shipping <span className="italic">& Delivery</span>
        </h1>
        <p
          className="mt-10 sm:mt-12 text-base sm:text-[17px] leading-[1.85]"
          style={{ color: body, ...reveal(topInView, 160), maxWidth: "600px" }}
          data-testid="sd-intro"
        >
          We carefully prepare each order to ensure it arrives in perfect condition.
        </p>
      </div>

      {/* Sections */}
      <div className="mt-20 sm:mt-24 space-y-16 sm:space-y-20">
        <Section title="Order Processing" testid="sd-processing">
          <p>
            Orders are typically processed and dispatched within{" "}
            <span style={{ color: "#0E0E0D" }}>2–5 business days</span> from the date of purchase.
          </p>
        </Section>

        <Section title="Delivery Timelines" testid="sd-timelines">
          <p>
            Once dispatched, orders are generally delivered within{" "}
            <span style={{ color: "#0E0E0D" }}>7 business days</span>, depending on the delivery
            location and courier service availability.
          </p>
        </Section>

        <Section title="Shipping Charges" testid="sd-charges">
          <p
            className="font-display"
            style={{ fontSize: "clamp(1.125rem, 1.4vw, 1.25rem)", color: "#0E0E0D" }}
          >
            Free shipping across Delhi.
          </p>
          <p>Shipping charges for orders outside Delhi will be calculated at checkout.</p>
        </Section>

        <Section title="Order Tracking" testid="sd-tracking">
          <p>
            Once your order has been dispatched, you will receive a tracking link via email to
            monitor your shipment's progress.
          </p>
        </Section>

        <Section title="Delivery Delays" testid="sd-delays">
          <p>
            While we strive to meet all delivery timelines, occasional delays may occur due to
            unforeseen circumstances, public holidays, weather conditions, or courier disruptions.
          </p>
        </Section>
      </div>

      {/* Need Assistance */}
      <section
        className="mt-28 sm:mt-36 px-8 sm:px-12 py-14 sm:py-16 text-center"
        style={{ background: "#F4F0E8" }}
        data-testid="sd-need-assistance"
      >
        <h2
          className="font-display"
          style={{
            fontSize: "clamp(1.5rem, 2.25vw, 2rem)",
            fontWeight: 400,
            letterSpacing: "-0.005em",
            color: "#0E0E0D",
          }}
        >
          Need Assistance?
        </h2>
        <p
          className="mt-6 text-base sm:text-[17px] leading-[1.85] mx-auto"
          style={{ color: body, maxWidth: "560px" }}
        >
          For questions regarding shipping or your order, please contact{" "}
          <a
            href="mailto:support@tamorebytamanna.com"
            className="underline underline-offset-4 hover:opacity-70"
            style={{ color: "#0E0E0D" }}
          >
            support@tamorebytamanna.com
          </a>
          .
        </p>
        <p className="mt-4 text-sm" style={{ color: muted }}>
          Our Client Care team is available Monday–Friday. We aim to respond within 24–48 business hours.
        </p>
      </section>
    </main>
  );
}
